import {
  DisplayProperties,
  FormViewField,
  SubmissionValue,
  ValidationProperties,
} from '@wix/forms-ui/types';
import { FieldType } from '@wix/ambassador-forms-v2-form/types';
import { getDefaultValue } from './service-form.mapper';
import {
  AddressFields,
  FormField,
  ValueType,
} from '@wix/ambassador-services-catalog-server/http';
import { BookingRequestKeyMappings } from './form-submission.mapper';
import { FieldLayout } from '../../types/types';

export type BookingsKeyMapping = {
  key: BookingRequestKeyMappings;
};

export const createFormField = ({
  fieldType,
  field,
  layout,
  bookingsKeyMapping,
  displayProperties,
  validations,
  metadata,
}: {
  fieldType: FieldType;
  layout?: FieldLayout;
  field?: FormField;
  bookingsKeyMapping?: BookingsKeyMapping;
  validations?: ValidationProperties;
  displayProperties?: DisplayProperties;
  metadata?: { [key: string]: any };
}): FormViewField => {
  return {
    externalId: field?.fieldId,
    renderInfo: {
      type: fieldType,
      displayProperties: {
        ...getCheckboxDisplayProps({
          field,
          fieldType,
        }),
        label: field?.label?.trim(),
        ...displayProperties,
      },
      metadata: {
        ...(metadata ? metadata : {}),
        ...(bookingsKeyMapping ? { bookingsKeyMapping } : {}),
        ...(layout
          ? {
              layout: { appearance: layout },
            }
          : {}),
      },
      validationProperties: {
        ...(validations ? validations : {}),
        required: field?.userConstraints?.required,
      },
    },
  };
};

export const createAddressField = (
  addressField: AddressFields,
  preFilledValues?: {
    [k: string]: SubmissionValue;
  },
) => {
  return createFormField({
    fieldType: FieldType.MULTI_LINE_ADDRESS,
    field: { fieldId: BookingRequestKeyMappings.FULL_ADDRESS },
    bookingsKeyMapping: {
      key: BookingRequestKeyMappings.FULL_ADDRESS,
    },
    displayProperties: {
      multiLineAddressSettings: {
        state: createFormField({
          fieldType: FieldType.TEXT,
          field: addressField.state,
          displayProperties: {
            defaultValue: getDefaultValue(
              preFilledValues,
              addressField.state?.fieldId!,
            ),
          },
        }).renderInfo,
        city: createFormField({
          fieldType: FieldType.TEXT,
          field: addressField.city,
          displayProperties: {
            defaultValue: getDefaultValue(
              preFilledValues,
              addressField.city?.fieldId!,
            ),
          },
        }).renderInfo,
        streetLine1: createFormField({
          fieldType: FieldType.TEXT,
          field: addressField.street,
          displayProperties: {
            defaultValue: getDefaultValue(
              preFilledValues,
              addressField.street?.fieldId!,
            ),
          },
        }).renderInfo,
        streetLine2: createFormField({
          fieldType: FieldType.TEXT,
          field: addressField.floorNumber,
          displayProperties: {
            defaultValue: getDefaultValue(
              preFilledValues,
              addressField.floorNumber?.fieldId!,
            ),
          },
        }).renderInfo,
      },
      defaultValue: getDefaultValue(
        preFilledValues,
        BookingRequestKeyMappings.FULL_ADDRESS,
      ),
    },
  });
};

export const getFieldType = (valueType: ValueType) => {
  switch (valueType) {
    case ValueType.CHECK_BOX:
      return FieldType.CHECKBOX;
    case ValueType.LONG_TEXT:
      return FieldType.PARAGRAPH;
    case ValueType.SHORT_TEXT:
      return FieldType.TEXT;
    default:
      return FieldType.TEXT;
  }
};

const getCheckboxDisplayProps = ({
  field,
  fieldType,
}: {
  field?: FormField;
  fieldType: FieldType;
}) => {
  const settings = {
    checkboxSettings: {
      link: {
        external: { url: field?.additionalLabels?.[0]?.linkLabel?.url },
      },
      linkLabel: field?.additionalLabels?.[0]?.linkLabel?.label,
    },
  };
  return fieldType === FieldType.CHECKBOX
    ? {
        ...settings,
      }
    : {};
};
